import { get, isNull, merge } from 'lodash';
import { UpdateConfigPayload } from 'src/components/modal/screenConfigModal/ScreenConfigModal.interface';
import { queryClient } from 'src/components/queryProvider';
import {
  CodPosition,
  CodPositions,
  DRIVE_THRU_1_SCREEN_NAME,
  DRIVE_THRU_2_SCREEN_NAMES,
  DRIVE_THRU_3_SCREEN_NAMES,
  ScreenLayoutTypeEnum,
  ScreenLayoutTypeFor2Screens,
} from 'src/constants';
import { IOrientation } from 'src/features/assemblies/addAssemblyPage/modules/selectLayoutForm/SelectLayoutForm.interface';
import {
  ApiResponse,
  CCApiResponse,
  IGridOptions,
  ILayout,
  ILayoutType,
  IScreen,
  IScreenOrientation,
  ISection,
  LayoutTypes,
  ScreenSize,
  SectionTypes,
} from 'src/interfaces';
import { generateQuery, lang, queryKeys, requestCCApi } from 'src/utils';
import { getSingleScreenConfigurationLabel } from 'src/utils/screen-config';

import { getOrCreateSectionId } from './useCreateScreenSection';

export const horizontalGridOptions = [
  { label: IGridOptions.GRID_4X2, value: IGridOptions.GRID_4X2 },
];
export const verticalGridOptions = [
  { label: IGridOptions.GRID_3X3, value: IGridOptions.GRID_3X3 },
  { label: IGridOptions.GRID_3X4, value: IGridOptions.GRID_3X4 },
];

export const mapLayoutScreens = (
  layoutScreens: CCApiResponse,
  screens: IScreen[],
): ILayout[] => {
  const layouts = get(layoutScreens, 'includes.layouts.data', []) as ILayout[];
  const layoutTypes = get(
    layoutScreens,
    'includes.layouts.includes.layoutTypes.data',
    [],
  ) as ILayoutType[];

  const formattedLayouts = formatLayoutsWithLayoutTypes(layouts, layoutTypes);

  return layoutScreens.data.reduce(
    (allLayouts: ILayout[], layoutScreen: unknown) => {
      const matchedLayout = allLayouts.find(
        (item) => item.id === get(layoutScreen, 'layout_id'),
      );

      if (matchedLayout) {
        if (!matchedLayout.screens) matchedLayout.screens = [];

        const matchedScreen = screens.find(
          (item) => item.id === get(layoutScreen, 'screen_id'),
        );

        if (matchedScreen) {
          matchedLayout.screens.push(
            merge(layoutScreen, matchedScreen, {
              layoutScreenId: get(layoutScreen, 'id'),
            }),
          );
        }

        matchedLayout.layoutType = layoutTypes.find(
          (item) => item.id === matchedLayout.layout_type_id,
        );
      }

      return allLayouts;
    },
    formattedLayouts,
  );
};

export const mapAllSections = (sections: ApiResponse[]): ISection[] => {
  return sections.map((section: ApiResponse) => {
    return mapSection(section);
  });
};

const mapSection = (section: ApiResponse): ISection => {
  return {
    id: section.id,
    name: section.name,
    width: section.width,
    height: section.height,
    created_at: section.created_at,
    modified_at: section.modified_at,
    left_offset: section.left_offset,
    top_offset: section.top_offset,
    settings: section.settings,
  };
};

export const mapScreenSections = (screenSections: CCApiResponse): IScreen[] => {
  const screens = get(screenSections, 'includes.screens.data', []) as IScreen[];
  const sections = get(
    screenSections,
    'includes.sections.data',
    [],
  ) as ISection[];

  return screenSections.data.reduce(
    (allScreens: IScreen[], screenSection: unknown) => {
      const matchedScreen = allScreens.find(
        (item) => item.id === get(screenSection, 'screen_id'),
      );

      if (matchedScreen) {
        if (!matchedScreen.sections) matchedScreen.sections = [];

        const matchedSection = sections.find(
          (item) => item.id === get(screenSection, 'section_id'),
        );

        if (matchedSection) {
          matchedScreen.sections.push(
            merge(screenSection, matchedSection, {
              screenSectionId: get(screenSection, 'id'),
            }),
          );
        }
      }

      return allScreens;
    },
    screens,
  );
};

export function generateSingleConfigString(payload: UpdateConfigPayload) {
  const grid: string = payload[`screen_grid`] ?? '';

  const hasDisclaimer = payload.disclaimer;
  let disclaimerString = '';

  if (hasDisclaimer) {
    const placement = payload[`placement`];
    const size = payload[`size`];

    if (placement && size) {
      disclaimerString = `With${placement}${size}Disclaimer`;
    }
  }

  const orientation = payload[`orientation`];

  return `${orientation}${grid
    .replace(/ /g, '')
    .toLowerCase()}Grid${disclaimerString}`;
}

export const getScreenId = async (orientation: string) => {
  const url = `/screens?pageSize=0&name.regexp=placeholder${orientation}`;

  const response = await requestCCApi({
    url,
    method: 'GET',
  });

  if (response.data.length === 0) {
    const isVertical = orientation === IOrientation.VERTICAL;
    const orientationHeight = isVertical ? 1920 : 1080;
    const orientationWidth = isVertical ? 1080 : 1920;
    const defaultScreen = await requestCCApi({
      url: `/screens`,
      method: 'POST',
      data: {
        data: {
          name: `placeholder${orientation}`,
          section_type_id: 1,
          width: orientationWidth,
          height: orientationHeight,
        },
      },
    });

    await requestCCApi({
      url: `/screenSections`,
      method: 'POST',
      data: {
        data: {
          section_id: getOrCreateSectionId({
            height: orientationHeight,
            width: orientationWidth,
          }),
          left_offset: 0,
          screen_id: defaultScreen.id,
          top_offset: 0,
        },
      },
    });

    return defaultScreen.id;
  } else {
    return response.data?.[0]?.id;
  }
};

export const getOrCreateLayoutTagId = async (layoutTagName: string) => {
  try {
    const url = `/layoutTags${generateQuery(
      [],
      [`name.list=${layoutTagName}`],
    )}`;
    const response = await requestCCApi({ url, method: 'GET' });

    if (response?.data?.length > 0) return response.data[0]?.id;

    const newLayoutTag = await requestCCApi({
      url: `/layoutTags`,
      method: 'POST',
      data: {
        data: {
          name: layoutTagName,
        },
      },
    });

    queryClient.invalidateQueries(queryKeys.layoutTags.invalidateAll());

    return newLayoutTag?.id;
  } catch (error) {
    console.error('Failed to fetch or create layout tag', error);
    throw error;
  }
};

export const getGridOptions = (screen: ScreenSize) => {
  const orientation = getSingleScreenConfigurationLabel(screen);
  if (orientation === IScreenOrientation.HORIZONTAL) {
    return horizontalGridOptions;
  }

  return verticalGridOptions;
};

export const advancedSettingsLayoutTags = [
  SectionTypes.COD,
  SectionTypes.DY,
  SectionTypes.TAKE_OVER,
  SectionTypes.SECTION_ROTATION,
];

export const getLayoutTagName = (name: string): string => {
  switch (name) {
    case SectionTypes.COD:
      return lang.editLayoutsSidebar.cod;
    case SectionTypes.DY:
      return lang.assemblyLayouts.advancedSettings.dynamicSection;
    case SectionTypes.SECTION_ROTATION:
      return lang.assemblyLayouts.advancedSettings.rotationSection;
    case SectionTypes.TAKE_OVER:
      return lang.editLayoutsSidebar.takeOver;
    default:
      return name;
  }
};

export const formatLayoutsWithLayoutTypes = (
  layouts: ILayout[],
  layoutTypes: ILayoutType[],
) => {
  const layoutTypesMap = layoutTypes.reduce((acc, type) => {
    acc[type.id] = type;

    return acc;
  }, {} as Record<string | number, ILayoutType>);

  return layouts.map((layout) => ({
    ...layout,
    layoutType: layoutTypesMap[get(layout, 'layout_type_id')],
  }));
};

export const getScreenName = (
  layoutType: ILayoutType,
  codPosition: CodPosition = CodPositions.LEFT,
  screenIndex: number = 1,
  totalScreens: number = 3,
  screenLayoutType?: string,
) => {
  let cp = isNull(codPosition) ? CodPositions.LEFT : codPosition;

  if (layoutType.name === LayoutTypes.OUTDOOR) {
    let screenNames: Record<number, string>;
    switch (totalScreens) {
      case 3:
        screenNames = DRIVE_THRU_3_SCREEN_NAMES[cp];
        break;
      case 2:
        screenNames =
          DRIVE_THRU_2_SCREEN_NAMES[cp][
            (screenLayoutType as ScreenLayoutTypeFor2Screens) ||
              ScreenLayoutTypeEnum.PRESELL_MENU
          ];
        break;
      case 1:
        screenNames = DRIVE_THRU_1_SCREEN_NAME;
        break;
      default:
        screenNames = DRIVE_THRU_3_SCREEN_NAMES[cp];
        break;
    }

    return screenNames[screenIndex];
  } else {
    // Indoor layouts are not affected by drive-thru hand (COD position), so screens
    // are always numbered in ascending order regardless of codPosition.
    return `Screen ${screenIndex}`;
  }
};
